<template>
  <main class="container-1 terrain-crews">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <TabNav :links="routesNav" />
    </div>

    <section class="container-body terrain-crews__body" aria-live="polite">
      <div class="terrain-crews__toolbar">
        <div class="terrain-crews__filters">
          <label class="terrain-crews__search">
            Buscar por nombre
            <input v-model="search" type="search" placeholder="Buscar cuadrilla…">
          </label>
          <label class="terrain-crews__status-filter">
            Estado
            <TerrainSelect v-model="activeFilter" :options="activeOptions" aria-label="Filtrar cuadrillas por estado" />
          </label>
        </div>
        <router-link class="terrain-crews__new" :to="{ name: 'terrain-crew-new' }">
          Nueva cuadrilla
        </router-link>
      </div>

      <p v-if="loading" class="terrain-crews__status">Cargando cuadrillas…</p>
      <p v-else-if="errorMessage" class="terrain-crews__error" role="alert">{{ errorMessage }}</p>
      <template v-else>
        <p v-if="!crews.length" class="terrain-crews__empty">
          {{ search || activeFilter !== 'all' ? 'Ninguna cuadrilla coincide con el filtro.' : 'No hay cuadrillas creadas todavía.' }}
        </p>
        <ul v-else class="terrain-crews__list" aria-label="Cuadrillas de terreno">
          <li v-for="crew in crews" :key="crew.id" class="terrain-crews__card">
            <div class="terrain-crews__info">
              <h3>{{ crew.name }}</h3>
              <p v-if="crew.description">{{ crew.description }}</p>
              <p class="terrain-crews__meta">
                {{ crew.memberUserIds.length }} miembro{{ crew.memberUserIds.length === 1 ? '' : 's' }}
                <TerrainBadge :type="crew.active ? 'success' : 'secondary'" :message="crew.active ? 'Activa' : 'Inactiva'" />
              </p>
            </div>
            <div class="terrain-crews__actions">
              <router-link
                class="terrain-crews__link"
                :to="{ name: 'terrain-crew-edit', params: { crewId: crew.id } }"
              >Editar</router-link>
              <button type="button" :disabled="statusBusy === crew.id" @click="confirmToggleStatus(crew)">
                {{ crew.active ? 'Desactivar' : 'Activar' }}
              </button>
            </div>
          </li>
        </ul>

        <Pagination
          v-if="totalPages > 1"
          :total-pages="totalPages"
          :current-page="page"
          :page-size="pageSize"
          @pagechanged="onPageChanged"
        />
      </template>
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import TabNav from "@/components/TabNav.vue";
import TerrainBadge from "@/components/terrain/TerrainBadge.vue";
import Pagination from "@/components/Pagination.vue";
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import { createTerrainApi } from "@/services/terrain/api.js";
import { terrainNavLinks } from "@/services/terrain/nav.js";

export default {
  name: "TerrainCrews",
  components: { Breadcrumb, TabNav, TerrainBadge, Pagination, TerrainSelect },
  data() {
    return {
      activeOptions: [
        { value: "all", label: "Todas" }, { value: "active", label: "Activas" }, { value: "inactive", label: "Inactivas" },
      ],
      crews: [],
      loading: true,
      errorMessage: "",
      search: "",
      activeFilter: "all",
      page: 1,
      pageSize: 10,
      totalPages: 0,
      statusBusy: null,
      searchTimer: null,
    };
  },
  computed: {
    routesBread() { return [{ name: "Terreno" }, { name: "Cuadrillas" }]; },
    routesNav() { return terrainNavLinks((this.$store.getters.getPermissions || {}).terrain); },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.loadCrews(); }, 300);
    },
    activeFilter() { this.page = 1; this.loadCrews(); },
  },
  created() { this.loadCrews(); },
  beforeDestroy() { clearTimeout(this.searchTimer); },
  methods: {
    async loadCrews() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.terrainApi.listCrews({
          page: this.page,
          size: this.pageSize,
          search: this.search.trim() || undefined,
          active: this.activeFilter === "all" ? undefined : this.activeFilter === "active",
        });
        this.crews = response.data.items || [];
        this.totalPages = response.data.totalPages || 0;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar las cuadrillas. Intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) {
      this.page = page;
      this.loadCrews();
    },
    confirmToggleStatus(crew) {
      const action = crew.active ? "desactivar" : "activar";
      this.$modal.show("dialog", {
        text: `¿Desea ${action} la cuadrilla "${crew.name}"?`,
        buttons: [
          { title: "Cancelar" },
          {
            title: crew.active ? "Desactivar" : "Activar",
            handler: () => { this.$modal.hide("dialog"); this.toggleStatus(crew); },
          },
        ],
      });
    },
    async toggleStatus(crew) {
      this.statusBusy = crew.id;
      try {
        await this.terrainApi.updateCrewStatus(crew.id, { expectedVersion: crew.resourceVersion, active: !crew.active });
        this.$store.dispatch("toast/show", { message: "Estado de la cuadrilla actualizado.", type: "success" });
        await this.loadCrews();
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.code;
        const message = code === "TERRAIN_STALE_RESOURCE"
          ? "La cuadrilla fue modificada por otra solicitud; recargue e intente de nuevo."
          : "No se pudo actualizar el estado de la cuadrilla.";
        this.$store.dispatch("toast/show", { message, type: "error" });
      } finally {
        this.statusBusy = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-crews__body {
  display: grid;
  gap: var(--terrain-space-lg);
  margin: 0 auto;
  max-width: 960px;
  width: 95%;
}

.terrain-crews__toolbar {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
  justify-content: space-between;
}

.terrain-crews__new {
  background: var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  text-decoration: none;
  transition: background-color var(--terrain-transition);
  white-space: nowrap;

  &:hover { background: var(--color-steel); text-decoration: none; }
}

.terrain-crews__filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-lg);
}

.terrain-crews__search,
.terrain-crews__status-filter {
  display: grid;
  gap: var(--terrain-space-xs);
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);
}

.terrain-crews__search { max-width: 320px; flex: 1; }
.terrain-crews__status-filter { min-width: 180px; }

.terrain-crews__search input {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--color-white);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

.terrain-crews__list { display: grid; gap: var(--terrain-space-md); list-style: none; margin: 0; padding: 0; }

.terrain-crews__card {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  display: flex;
  gap: var(--terrain-space-lg);
  justify-content: space-between;
  padding: var(--terrain-space-lg);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);
  flex-wrap: wrap;

  &:hover { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
}

.terrain-crews__info h3 { margin: 0; color: var(--color-navy); }
.terrain-crews__info p { margin: var(--terrain-space-xs) 0 0; color: var(--color-ghost); }
.terrain-crews__meta { display: flex; align-items: center; gap: var(--terrain-space-sm); }

.terrain-crews__actions { display: flex; flex-wrap: wrap; gap: var(--terrain-space-sm); }

.terrain-crews__link,
.terrain-crews__actions button {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-navy);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  text-decoration: none;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .5; }
}

.terrain-crews__error {
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
  margin: 0;
  padding: var(--terrain-space-md);
}

.terrain-crews__status,
.terrain-crews__empty {
  color: var(--color-ghost);
  margin: 0;
}

@media (max-width: 640px) {
  .terrain-crews__card { flex-direction: column; align-items: stretch; }
}
</style>
